import {EMAIL_SENDINGS} from '../constants/collections'

const fieldFilter = (fieldPath, op, value) => ({fieldFilter: {field: {fieldPath}, op, value}})
const equals = (field, value) => fieldFilter(field, 'EQUAL', {stringValue: value})
const positive = field => fieldFilter(field, 'GREATER_THAN', {integerValue: '0'})

const decodeFields = fields => Object.fromEntries(Object.entries(fields || {}).map(([key, value]) => [key, decodeValue(value)]))
const decodeValue = value => {
  if('mapValue' in value) return decodeFields(value.mapValue.fields)
  if('arrayValue' in value) return (value.arrayValue.values || []).map(decodeValue)
  if('integerValue' in value) return Number(value.integerValue)
  if('doubleValue' in value) return Number(value.doubleValue)
  if('nullValue' in value) return null
  // Tracking records otherwise contain strings and booleans. Preserve other
  // scalar Firestore values as text/objects rather than dropping optional data.
  return Object.values(value)[0]
}

const queryError = ({status, message} = {}) => {
  const error = new Error(message || 'Unable to load email tracking.')
  error.code = (status || 'unknown').toLowerCase().replace(/_/g, '-')
  return error
}

// Firebase 9.14 rejects OR and non-leading inequality filters locally. Use the
// supported REST query API with the same Firebase user token and security rules.
// https://firebase.google.com/docs/firestore/use-rest-api
export const fetchFilteredEmailSendings = async ({auth, projectId, cursor, status, environment, pageSize}) => {
  const user = auth.currentUser
  if(!user) throw queryError({status: 'UNAUTHENTICATED'})

  const filters = []
  const sortFields = ['meta.updated']
  if(environment) {
    if(!['production', 'staging', 'development'].includes(environment)) throw new Error('Unknown email environment.')
    filters.push(equals('environment', environment))
  }
  if(status === 'any_failure') {
    filters.push({compositeFilter: {op: 'OR', filters: [
      equals('submission_status', 'failed'),
      positive('delivery_counts.permanent_failure'),
      positive('delivery_counts.temporary_failure'),
    ]}})
    sortFields.push('delivery_counts.permanent_failure', 'delivery_counts.temporary_failure')
  } else if(['permanent_failure', 'temporary_failure', 'delivered'].includes(status)) {
    filters.push(positive(`delivery_counts.${status}`))
    sortFields.push(`delivery_counts.${status}`)
  } else if(['failed', 'unknown', 'pending', 'accepted'].includes(status)) {
    filters.push(equals('submission_status', status))
  } else if(status) {
    throw new Error('Unknown email sending status filter.')
  }
  // Include every ordering value in the cursor, plus the document name, so
  // records with equal update times remain distinct across page boundaries.
  sortFields.push('__name__')
  const structuredQuery = {
    from: [{collectionId: EMAIL_SENDINGS}],
    orderBy: sortFields.map(fieldPath => ({field: {fieldPath}, direction: 'DESCENDING'})),
    limit: pageSize + 1,
  }
  if(filters.length) structuredQuery.where = filters.length === 1 ? filters[0] : {compositeFilter: {op: 'AND', filters}}
  if(cursor) structuredQuery.startAt = {values: cursor, before: false}

  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), 30000)
  try {
    const request = async forceRefresh => fetch(
      `https://firestore.googleapis.com/v1/projects/${encodeURIComponent(projectId)}/databases/(default)/documents:runQuery`,
      {
        method: 'POST',
        headers: {'Content-Type': 'application/json', Authorization: `Bearer ${await user.getIdToken(forceRefresh)}`},
        body: JSON.stringify({structuredQuery}),
        signal: controller.signal,
      },
    )
    let response = await request(false)
    if(response.status === 401) response = await request(true)
    const result = await response.json()
    // runQuery can also return an error within its streamed response array.
    const failure = Array.isArray(result) ? result.find(item => item.error)?.error : result.error
    if(!response.ok || failure) throw queryError(failure)
    if(!Array.isArray(result)) throw new Error('Invalid email tracking response.')
    const documents = result.filter(item => item.document).map(item => item.document)
    const displayed = documents.slice(0, pageSize)
    const last = displayed[displayed.length - 1]
    return {
      records: displayed.map(document => ({...decodeFields(document.fields), id: document.name.split('/').pop()})),
      nextCursor: last ? sortFields.map(field => field === '__name__'
        ? {referenceValue: last.name}
        : field.split('.').reduce((value, key) => value.mapValue.fields[key], {mapValue: {fields: last.fields}})) : null,
      hasNext: documents.length > pageSize,
    }
  } finally {
    clearTimeout(timeout)
  }
}
